import React, { useEffect } from "react";
import { FaTimesCircle } from "react-icons/fa";

import type { AdminBusinessTransaction } from "../../lib/api/adminBusinessTransactions";
import { formatAmount } from "../../utils/FormatPerson";
import {
  assetLabelFor,
  businessStatusBadgeClass,
  getMerchantDisplayDate,
  merchantStatusBadgeClass,
  ownerFromTx,
} from "./merchantTransactionsTable";

type Props = {
  transaction: AdminBusinessTransaction;
  onClose: () => void;
};

type Field = { label: string; value: React.ReactNode; full?: boolean };

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

function dash(value: string): string {
  return value.trim() || "—";
}

function Section({ title, fields }: { title: string; fields: Field[] }) {
  const visible = fields.filter((f) => f.value !== null && f.value !== undefined);
  if (visible.length === 0) return null;
  return (
    <section className="mt-6 first:mt-0">
      <h3 className="text-[11px] font-bold uppercase tracking-widest text-[#869AA9] mb-3">
        {title}
      </h3>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visible.map((f) => (
          <div
            key={f.label}
            className={`rounded-lg border border-[#2C363E]/60 bg-[#1C2227]/80 px-3 py-3 ${
              f.full ? "sm:col-span-2" : ""
            }`}
          >
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
              {f.label}
            </dt>
            <dd className="mt-1 text-[13px] font-semibold text-[#E7EBEE] break-words">
              {f.value}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

const MerchantTransactionDetailModal: React.FC<Props> = ({
  transaction,
  onClose,
}) => {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const isDeposit =
    str(transaction.transactionType).toLowerCase() !== "payout" &&
    !transaction.recipient;

  const business = obj(transaction.business);
  const owner = ownerFromTx(transaction);
  const ownerRaw = obj(transaction.owner);
  const page = obj(transaction.paymentPage);
  const recipient = obj(transaction.recipient);
  const provider = obj(transaction.provider);
  const asset = obj(transaction.asset);

  const usdValue =
    num(transaction.usdtAmount) ??
    num(asset?.amountInUSD) ??
    num(transaction.amountInUSD);
  const payoutNgn = num(transaction.payoutAmount);
  const fees = num(transaction.fees);

  const txFields: Field[] = [
    {
      label: "Status",
      value: (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${merchantStatusBadgeClass(
            transaction.status
          )}`}
        >
          {dash(str(transaction.status))}
        </span>
      ),
    },
    { label: "Date", value: getMerchantDisplayDate(transaction) },
    { label: "Asset", value: assetLabelFor(transaction) },
    ...(usdValue != null
      ? [{ label: "USD value", value: `$${formatAmount(usdValue)}` }]
      : []),
    ...(payoutNgn != null
      ? [{ label: "Payout amount", value: `₦${formatAmount(payoutNgn)}` }]
      : []),
    ...(fees != null ? [{ label: "Fees", value: formatAmount(fees) }] : []),
    ...(str(transaction.network)
      ? [{ label: "Network", value: str(transaction.network).toUpperCase() }]
      : []),
    ...(typeof transaction.isAutomated === "boolean"
      ? [
          {
            label: "Automated",
            value: transaction.isAutomated ? "Yes" : "No",
          },
        ]
      : []),
    {
      label: "Transaction ID",
      value: <span className="font-mono text-[12px]">{transaction._id}</span>,
      full: true,
    },
    ...(str(transaction.reference)
      ? [
          {
            label: "Reference",
            value: (
              <span className="font-mono text-[12px]">
                {str(transaction.reference)}
              </span>
            ),
            full: true,
          },
        ]
      : []),
    ...(str(transaction.txid)
      ? [
          {
            label: "Txid",
            value: (
              <span className="font-mono text-[12px]">{str(transaction.txid)}</span>
            ),
            full: true,
          },
        ]
      : []),
    ...(str(transaction.toAddress)
      ? [
          {
            label: "To address",
            value: (
              <span className="font-mono text-[12px]">
                {str(transaction.toAddress)}
              </span>
            ),
            full: true,
          },
        ]
      : []),
    ...(provider && str(provider.name)
      ? [{ label: "Provider", value: str(provider.name) }]
      : []),
  ];

  const businessFields: Field[] = business
    ? [
        { label: "Name", value: dash(str(business.name)) },
        { label: "Email", value: dash(str(business.email)) },
        { label: "Account type", value: dash(str(business.type)) },
        {
          label: "Status",
          value: (
            <span
              className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${businessStatusBadgeClass(
                business.status
              )}`}
            >
              {dash(str(business.status))}
            </span>
          ),
        },
        {
          label: "Verified",
          value: business.isVerified === true ? "Yes" : "No",
        },
        {
          label: "Industry",
          value: dash(str(obj(business.details)?.industry)),
        },
        {
          label: "Country",
          value: dash(str(obj(business.details)?.country)),
        },
        {
          label: "Address",
          value: dash(str(obj(business.details)?.address)),
          full: true,
        },
      ]
    : [];

  const ownerFields: Field[] = ownerRaw
    ? [
        { label: "Name", value: owner.name },
        {
          label: "Email",
          value: dash(str(obj(ownerRaw.email)?.address)),
        },
        {
          label: "Username",
          value: dash(str(obj(ownerRaw.profile)?.username)),
        },
        {
          label: "User ID",
          value: (
            <span className="font-mono text-[12px]">{dash(str(ownerRaw._id))}</span>
          ),
          full: true,
        },
      ]
    : [];

  const pageFields: Field[] = page
    ? [
        { label: "Page name", value: dash(str(page.name)) },
        ...(num(page.amount) != null
          ? [{ label: "Page amount", value: `₦${formatAmount(num(page.amount))}` }]
          : []),
        {
          label: "Reference",
          value: (
            <span className="font-mono text-[12px]">{dash(str(page.reference))}</span>
          ),
          full: true,
        },
      ]
    : [];

  const recipientFields: Field[] = recipient
    ? [
        { label: "Account name", value: dash(str(recipient.accountName)) },
        {
          label: "Account number",
          value: (
            <span className="font-mono text-[12px]">
              {dash(str(recipient.accountNumber))}
            </span>
          ),
        },
        { label: "Bank", value: dash(str(recipient.bankName)) },
        { label: "Bank code", value: dash(str(recipient.bankCode)) },
      ]
    : [];

  return (
    <div
      className="fixed inset-0 z-50 flex font-manrope justify-center items-start overflow-auto bg-black bg-opacity-70 px-4 pt-12 pb-12 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Merchant transaction details"
    >
      <div className="w-full max-w-3xl max-h-[calc(100vh-6rem)] flex flex-col rounded-xl border border-[#2C363E] bg-[#0D0D0D] p-6">
        <div className="flex shrink-0 justify-between items-start gap-4">
          <div>
            <h2 className="font-sora font-bold text-[20px] text-white">
              {isDeposit ? "Deposit" : "Payout"} details
            </h2>
            <p className="text-[12px] text-[#869AA9] mt-1">
              {dash(str(business?.name))}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            className="shrink-0"
          >
            <FaTimesCircle className="text-white text-[28px] cursor-pointer hover:opacity-80" />
          </button>
        </div>

        <div className="w-full h-[1px] shrink-0 bg-[#2C363E] mt-5" />

        <div className="flex-1 min-h-0 overflow-y-auto pt-5 pb-2">
          <Section title="Transaction" fields={txFields} />
          <Section title="Business" fields={businessFields} />
          <Section title="Owner" fields={ownerFields} />
          <Section title="Payment page" fields={pageFields} />
          <Section title="Recipient" fields={recipientFields} />
        </div>
      </div>
    </div>
  );
};

export default MerchantTransactionDetailModal;
